import React, { ButtonHTMLAttributes, ReactNode, useState } from 'react';
import { CaretDown } from '../icons';

type SecondaryButtonVariants = 'default' | 'dropdown' | 'outlined';

export interface SecondaryButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  className?: string;
  variant: SecondaryButtonVariants;
  disabled?: boolean;
}

/* Default styles for secondary buttons */
const SCDefaultStyle = `px-4 py-1.75 bg-thryv-gray-light-300 text-btn-text rounded-3px text-thryv-black-400 font-open-sans font-normal 
capitalize hover:bg-thryv-gray-medium-200 border 
border-thryv-gray-light-300 hover:border-thryv-gray-medium-200 disabled:bg-thryv-gray-light-500
disabled:border-thryv-gray-light-500 disabled:text-white disabled:font-semibold`;

/* Styles for secondary dropdown buttons */
const SCDropDownStyle = `px-4 py-1.75 min-w-143px flex flex-grow justify-between items-center 
bg-thryv-gray-light-300 text-btn-text rounded-3px text-thryv-black-400 font-open-sans font-normal capitalize hover:bg-thryv-gray-medium-200 active:bg-thryv-orange-500 active:text-white`;

/* Styles for secondary outlined buttons */
const SCOutlinedStyle = `px-4 py-1.75 bg-transparent text-thryv-black-500 text-btn-text border 
border-thryv-black-500 font-open-sans font-semibold capitalize rounded hover:bg-thryv-black-500 hover:text-white 
disabled:bg-transparent disabled:border-thryv-gray-medium-300 disabled:text-thryv-gray-medium-400`;

export const SecondaryButton = ({
  children,
  className,
  variant = 'default',
  disabled = false,
  type = 'button',
  ...rest
}: SecondaryButtonProps) => {
  const [active, setActive] = useState(false);

  const renderDefault =
    variant === 'default' ? (
      <button
        type={type}
        disabled={disabled}
        className={`${SCDefaultStyle} transition duration-200 ease-in-out ${
          className && className
        }`}
        {...rest}
      >
        {children}
      </button>
    ) : null;

  const renderDropDown =
    variant === 'dropdown' ? (
      <button
        type={type}
        disabled={disabled}
        className={`${SCDropDownStyle} transition duration-200 ease-in-out ${
          className && className
        }`}
        {...rest}
        onMouseDown={() => setActive(true)}
        onMouseUp={() => setActive(false)}
        onMouseLeave={() => setActive(false)}
      >
        {children}
        <CaretDown
          fill=""
          className={`fill-current ${
            !active ? 'text-thryv-black-400' : 'text-white'
          } ml-2`}
        />
      </button>
    ) : null;

  const renderOutlined =
    variant === 'outlined' ? (
      <button
        type={type}
        disabled={disabled}
        className={`${SCOutlinedStyle} transition duration-200 ease-in-out ${
          className && className
        }`}
        {...rest}
      >
        {children}
      </button>
    ) : null;

  return (
    <>
      {renderDefault}
      {renderDropDown}
      {renderOutlined}
    </>
  );
};
